import { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import LoadingButton from '@/components/ui/LoadingButton';
import { Modal, Form, ButtonGroup, ToggleButton } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { createDeposit } from '@/shared/store/slices/walletSlice';
import Axios, { markPageUnloading } from '@/shared/api/axios';

const DepositModal = forwardRef(({ onError }, ref) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const currentIdolId = useSelector(state => state.user.idolId);

  const [show, setShow] = useState(false);
  const [selectedAmount, setSelectedAmount] = useState('10');
  const [customAmount, setCustomAmount] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [customAmountError, setCustomAmountError] = useState('');

  // 充值設定（從 API 取得）。不給預設值：取不到就不讓使用者用錯的門檻送出
  const [minDepositAmount, setMinDepositAmount] = useState(null);
  const [loadingSettings, setLoadingSettings] = useState(false);
  const settingsReady = !loadingSettings && minDepositAmount !== null;

  // 取得充值設定
  useEffect(() => {
    const fetchSettings = async () => {
      if (!show || !currentIdolId) return;

      setLoadingSettings(true);
      try {
        const response = await Axios.get('/wallet/deposit-settings', {
          params: { idol_id: currentIdolId }
        });

        if (response.data?.status_code === 'SYSTEM_1000' && response.data.data) {
          setMinDepositAmount(parseFloat(response.data.data.min_deposit_amount));
        } else if (response.data?.status_code) {
          onError(response.data.status_code);
          handleClose();
        }
      } catch (error) {
        console.error('Failed to fetch deposit settings:', error);
        // NETWORK_ERROR 由全域 ServerErrorPage 處理，不另外顯示 modal
        if (error.httpErrorCode && error.httpErrorCode !== 'NETWORK_ERROR') {
          onError(error.httpErrorCode);
          handleClose();
        }
      } finally {
        setLoadingSettings(false);
      }
    };

    fetchSettings();
  }, [show, currentIdolId, onError]);

  useImperativeHandle(ref, () => ({
    show: () => {
      setSelectedAmount('10');
      setCustomAmount('');
      setCustomAmountError('');
      setMinDepositAmount(null);
      setShow(true);
    },
    hide: () => {
      setShow(false);
    }
  }));

  const handleClose = () => {
    setShow(false);
    setSelectedAmount('10');
    setCustomAmount('');
    setCustomAmountError('');
  };

  const handleConfirm = async () => {
    const amount = selectedAmount === 'custom' ? customAmount : selectedAmount;

    setIsSubmitting(true);

    try {
      const result = await dispatch(createDeposit({
        idolId: currentIdolId,
        amount
      })).unwrap();

      // 成功後跳轉到支付頁面
      if (result.payment_url) {
        markPageUnloading();
        window.location.href = result.payment_url;
      }

      handleClose();
    } catch (statusCode) {
      // 錯誤由 WalletPage 的 StatusModal 處理
      if (onError) {
        onError(statusCode);
      }
      handleClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAmountChange = (value) => {
    setSelectedAmount(value);
    if (value !== 'custom') {
      setCustomAmount('');
      setCustomAmountError('');
    }
  };

  const handleCustomAmountChange = (value) => {
    setCustomAmount(value);

    // 驗證邏輯
    if (!value || value === '') {
      setCustomAmountError('');
    } else {
      const amount = parseFloat(value);
      if (isNaN(amount) || amount <= 0) {
        setCustomAmountError(t('invalid_amount'));
      } else if (amount < minDepositAmount) {
        setCustomAmountError(t('minimum_deposit_error', { min: minDepositAmount }));
      } else {
        setCustomAmountError('');
      }
    }
  };

  const isConfirmDisabled = () => {
    if (!settingsReady) return true;
    if (selectedAmount === 'custom') {
      return !customAmount || isNaN(customAmount) || Number(customAmount) <= 0 || !!customAmountError;
    }
    return false;
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      backdrop={isSubmitting ? 'static' : true}
      keyboard={!isSubmitting}
    >
      <Modal.Header className="border-bottom pb-3" closeButton={!isSubmitting}>
        <Modal.Title className="fw-bold">{t('deposit_modal_title')}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {settingsReady ? (
          <>
          {/* 充值金額 */}
          <div className="mb-4">
            <Form.Label className="fw-bold">{t('deposit_amount_label')}</Form.Label>
            <div className="d-flex flex-column gap-2 w-100">
              <ButtonGroup className="w-100">
                {[
                  { value: '10', label: '₣10' },
                  { value: '30', label: '₣30' },
                  { value: '50', label: '₣50' },
                  { value: '100', label: '₣100' },
                  { value: 'custom', label: t('custom_amount') }
                ].map((option) => (
                  <ToggleButton
                    key={option.value}
                    id={`amount_${option.value}`}
                    type="radio"
                    variant={selectedAmount === option.value ? 'primary' : 'outline-primary'}
                    name="amount"
                    value={option.value}
                    checked={selectedAmount === option.value}
                    onChange={() => handleAmountChange(option.value)}
                    className="px-0"
                    disabled={isSubmitting}
                  >
                    {option.label}
                  </ToggleButton>
                ))}
              </ButtonGroup>
              {selectedAmount === 'custom' && (
                <div>
                  <Form.Control
                    type="number"
                    placeholder="FCoin"
                    value={customAmount}
                    onChange={(e) => handleCustomAmountChange(e.target.value)}
                    isInvalid={!!customAmountError}
                    disabled={isSubmitting}
                  />
                  {customAmountError && (
                    <div className="text-danger small mt-1">{customAmountError}</div>
                  )}
                </div>
              )}
              <div>
                <small className="text-muted">{t('fcoin_note')}</small>
              </div>
            </div>
          </div>

          {/* 支付方式 */}
          <div>
            <Form.Label className="fw-bold">{t('payment_method')}</Form.Label>
            <div className="d-flex align-items-start gap-2 mb-2">
              <Form.Check
                type="radio"
                name="payment"
                id="payment_nowpayments"
                checked
                readOnly
              />
              <div>
                <div>NowPayments</div>
                <small className="text-muted">{t('payment_method_note')}</small>
                <div><small className="text-muted">{t('minimum_deposit_amount')}₣{minDepositAmount} FCoin</small></div>
              </div>
            </div>
          </div>
          </>
        ) : (
          <div className="text-center py-5">
            <span className="spinner-border text-primary" role="status">
              <span className="visually-hidden">{t('loading')}</span>
            </span>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer className="border-0 d-flex justify-content-end gap-2">
        <button
          className="btn btn-neutral rounded-pill"
          onClick={handleClose}
          disabled={isSubmitting}
        >
          {t('cancel')}
        </button>
        <LoadingButton
          className="btn btn-primary rounded-pill"
          onClick={handleConfirm}
          disabled={isConfirmDisabled()}
          isLoading={isSubmitting}
        >
          {t('confirm')}
        </LoadingButton>
      </Modal.Footer>
    </Modal>
  );
});

DepositModal.displayName = 'DepositModal';

export default DepositModal;
